/**
 * Fixed-point decimal arithmetic on `bigint`.
 *
 * D7 and docs/04 §2.3: every monetary value, every quantity and every
 * probability crosses the wire as a decimal string, and **no float ever touches
 * a balance or a price**. §2.3.1 works three shipped failures in detail — a cost
 * basis that loses a micro-dollar, a flat minute that resolves DOWN because
 * floating-point addition is not associative, and a fee account that cannot be
 * reconciled. All three are prevented here rather than papered over with an
 * epsilon.
 *
 * The representation is a scaled `bigint` plus a decimal-place count carried
 * separately by the caller, because every wire field has a fixed, documented
 * scale (6dp for money and shares, 4dp for rates, probabilities and bps). A
 * value and its scale are never bundled into an object: the scale is a property
 * of the field, known at the call site, and pairing them would invite adding two
 * quantities that have no business being added.
 */

export class DecimalError extends Error {
  override readonly name = 'DecimalError'
}

/** USDC amounts and share quantities are exactly 6dp on the wire. */
export const USDC_DP = 6
export const SHARES_DP = 6
/** Rates, probabilities and basis points are exactly 4dp. */
export const RATE_DP = 4
export const PROBABILITY_DP = 4
export const BPS_DP = 4

/** Prices are integer cents in [1, 99] (D1). Not money — an index into a tick ladder. */
export const MIN_PRICE_CENTS = 1
export const MAX_PRICE_CENTS = 99

const POW10: readonly bigint[] = Array.from({ length: 40 }, (_, i) => 10n ** BigInt(i))

function pow10(n: number): bigint {
  if (n < 0) throw new DecimalError(`negative exponent ${n}`)
  const cached = POW10[n]
  return cached ?? 10n ** BigInt(n)
}

// A decimal string, optionally signed, with no exponent notation. Scientific
// notation and `Infinity` are not decimal strings and are not coerced into one.
const DECIMAL_RE = /^([+-]?)(\d+)(?:\.(\d+))?$/

/**
 * Parse a wire decimal string into a `bigint` scaled by `10 ** dp`.
 *
 * Fewer fraction digits than `dp` are accepted and zero-extended. **More are
 * rejected**, because silently truncating is how a micro-USDC goes missing, and
 * silently rounding is how two clients disagree about the same payload.
 */
export function parseFixed(value: string, dp: number): bigint {
  if (typeof value !== 'string') {
    throw new DecimalError(`expected a decimal string, got ${typeof value}`)
  }
  const m = DECIMAL_RE.exec(value)
  if (!m) throw new DecimalError(`not a decimal string: ${JSON.stringify(value)}`)

  const [, sign, whole, frac = ''] = m as unknown as [string, string, string, string?]
  if (frac.length > dp) {
    throw new DecimalError(
      `${JSON.stringify(value)} carries ${frac.length} decimal places; the field is ${dp}dp. ` +
        'Truncating here would lose money that the ledger will later ask for.',
    )
  }
  const digits = whole + frac.padEnd(dp, '0')
  const magnitude = BigInt(digits)
  return sign === '-' ? -magnitude : magnitude
}

/** Format a scaled `bigint` back to the wire form: always exactly `dp` places. */
export function formatFixed(value: bigint, dp: number): string {
  const negative = value < 0n
  const digits = (negative ? -value : value).toString().padStart(dp + 1, '0')
  const whole = digits.slice(0, digits.length - dp)
  const frac = dp === 0 ? '' : `.${digits.slice(digits.length - dp)}`
  return `${negative ? '-' : ''}${whole}${frac}`
}

/** Re-scale between two decimal-place counts. Throws rather than lose digits. */
export function rescale(value: bigint, fromDp: number, toDp: number): bigint {
  if (toDp === fromDp) return value
  if (toDp > fromDp) return value * pow10(toDp - fromDp)
  const divisor = pow10(fromDp - toDp)
  if (value % divisor !== 0n) {
    throw new DecimalError(`rescaling ${value} from ${fromDp}dp to ${toDp}dp would lose precision`)
  }
  return value / divisor
}

export function cmp(a: bigint, b: bigint): -1 | 0 | 1 {
  return a < b ? -1 : a > b ? 1 : 0
}

export function sum(values: readonly bigint[]): bigint {
  let total = 0n
  for (const v of values) total += v
  return total
}

export function absBig(v: bigint): bigint {
  return v < 0n ? -v : v
}

/** Ceiling division for a rational with a positive denominator. */
export function ceilDiv(numerator: bigint, denominator: bigint): bigint {
  if (denominator <= 0n) throw new DecimalError('denominator must be positive')
  const q = numerator / denominator
  const r = numerator % denominator
  return r > 0n ? q + 1n : q
}

// ---------------------------------------------------------------------------
// Money, in integers
// ---------------------------------------------------------------------------

/**
 * Cost of `sizeShares` (6dp micro-shares) at `priceCents`, in micro-USDC.
 *
 * `size_µ × cents / 100`, exact: `size_step` is `0.010000`, so `size_µ` is
 * always a multiple of 10,000 and the division by 100 never has a remainder
 * (docs/04 §2.3.1a). A remainder means the caller was handed a size that is not
 * on the step, which is a bug worth surfacing rather than rounding away.
 */
export function costMicroUsdc(sizeShares: bigint, priceCents: number): bigint {
  assertPriceCents(priceCents)
  const numerator = sizeShares * BigInt(priceCents)
  if (numerator % 100n !== 0n) {
    throw new DecimalError(
      `size ${formatFixed(sizeShares, SHARES_DP)} at ${priceCents}c is not an exact micro-USDC amount; ` +
        'the size is off the 0.010000 step.',
    )
  }
  return numerator / 100n
}

/**
 * A winning share redeems for exactly 1.000000 USDC (D1). Both quantities are
 * 6dp, so the payout is the share count unchanged — stated as a function
 * because the identity is a product decision, not an accident of scale.
 */
export function payoutMicroUsdc(sizeShares: bigint): bigint {
  return sizeShares
}

/**
 * `fee = ceil6(size × f × p × (1 − p))`, `p = price_cents / 100` (D12,
 * docs/04 §7.9).
 *
 * At launch `f = 0.0000` and every fee is exactly zero — which is precisely why
 * the arithmetic is built correctly now, while nothing is at stake, rather than
 * the week revenue starts depending on it. Computed as one exact rational:
 * `size_µ × rate_raw × cents × (100 − cents) / 10^8`, ceiling-rounded, so the
 * fee account reconciles to the micro-USDC forever.
 */
export function feeMicroUsdc(sizeShares: bigint, takerFeeRate4dp: bigint, priceCents: number): bigint {
  assertPriceCents(priceCents)
  if (takerFeeRate4dp < 0n) throw new DecimalError('fee rate cannot be negative')
  const cents = BigInt(priceCents)
  const numerator = sizeShares * takerFeeRate4dp * cents * (100n - cents)
  // 10^4 (rate scale) × 100 (p) × 100 (1 − p)
  return ceilDiv(numerator, 100_000_000n)
}

function assertPriceCents(priceCents: number): void {
  if (!Number.isInteger(priceCents) || priceCents < MIN_PRICE_CENTS || priceCents > MAX_PRICE_CENTS) {
    throw new DecimalError(`price_cents must be an integer in [1, 99]; got ${priceCents}`)
  }
}

// ---------------------------------------------------------------------------
// Strike and settlement observations
// ---------------------------------------------------------------------------

/**
 * The `{raw, expo}` pair that rides alongside every strike and settlement
 * value (D7).
 *
 * **`expo` is read from the payload, never assumed.** docs/05 is explicit that
 * the upstream stream is 8 **or** 18 decimals and that the `-8` in every worked
 * example is unverified; a client that hardcodes the 8-decimal scale factor
 * misreads an 18-decimal stream by ten orders of magnitude. Appendix C bans
 * both spellings of that constant, and the lint rule enforces it.
 */
export interface RawPrice {
  /** An integer, as a decimal string — at `expo = -8` the mantissa is only just inside 2^53. */
  raw: string
  /** Negative decimal exponent: the value is `raw × 10 ** expo`. */
  expo: number
}

/**
 * Compare two observations exactly, normalising to the finer exponent first.
 * Never compares `value` strings lexically and never parses either into a float
 * (docs/04 §2.3.1b — a flat minute must not resolve DOWN because two correct
 * implementations summed the same prices in different orders).
 */
export function comparePrices(a: RawPrice, b: RawPrice): -1 | 0 | 1 {
  const target = Math.min(a.expo, b.expo)
  return cmp(normaliseRaw(a, target), normaliseRaw(b, target))
}

function normaliseRaw(price: RawPrice, targetExpo: number): bigint {
  if (!Number.isInteger(price.expo)) throw new DecimalError(`expo must be an integer; got ${price.expo}`)
  if (!/^[+-]?\d+$/.test(price.raw)) throw new DecimalError(`raw must be an integer string; got ${price.raw}`)
  return BigInt(price.raw) * pow10(price.expo - targetExpo)
}

export type Outcome = 'UP' | 'DOWN'

/**
 * The tie rule, in one integer comparison: **`UP` iff `settle >= strike`**
 * (D5). Tie goes to UP, matching the incumbent's published rules text, and
 * there is deliberately no tolerance band — a tolerance re-introduces a region
 * where the outcome is undefined, which is a manipulation target.
 */
export function outcomeOf(settlement: RawPrice, strike: RawPrice): Outcome {
  return comparePrices(settlement, strike) >= 0 ? 'UP' : 'DOWN'
}
